/**
 * Admin chat unread count for navbar dot and dashboard notifications.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { adminFetch } from '../utils/adminFetch';

export interface AdminChatUnreadState {
  count: number;
  loading: boolean;
  refresh: () => void;
}

const defaultState: AdminChatUnreadState = {
  count: 0,
  loading: true,
  refresh: () => {},
};

const AdminChatUnreadContext = createContext<AdminChatUnreadState>(defaultState);

export const AdminChatUnreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!API_BASE_URL) {
      setLoading(false);
      return;
    }
    adminFetch(`${API_BASE_URL}/api/admin/chat/unread-count`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('unread count failed'))))
      .then((data: { count?: number }) => {
        setCount(typeof data.count === 'number' ? data.count : Number(data.count) || 0);
      })
      .catch(() => setCount(0))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 8000);
    return () => window.clearInterval(id);
  }, [refresh]);

  return (
    <AdminChatUnreadContext.Provider value={{ count, loading, refresh }}>
      {children}
    </AdminChatUnreadContext.Provider>
  );
};

export const useAdminChatUnread = (): AdminChatUnreadState => useContext(AdminChatUnreadContext);
